import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { dashboardApi, dashboardQueryKeys } from '../api/dashboard.api';

interface YearlyPlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function YearlyPlanModal({ open, onOpenChange }: YearlyPlanModalProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [amount, setAmount] = React.useState('');

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: dashboardQueryKeys.yearlyPlan(),
    queryFn: dashboardApi.getYearlyPlan,
  });

  React.useEffect(() => {
    if (open && plan?.amount != null) {
      setAmount(String(plan.amount));
    } else if (open && !planLoading) {
      setAmount('');
    }
  }, [open, plan, planLoading]);

  const mutation = useMutation({
    mutationFn: (value: number) => dashboardApi.saveYearlyPlan(value, plan?.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.yearlyPlan() });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });
      onOpenChange(false);
    },
  });

  const isEdit = !!plan?.amount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) return;
    mutation.mutate(parsed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-[#0069D1]" />
            {isEdit ? t('dashboard.editYearlyPlan') : t('dashboard.addYearlyPlan')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="yearly-plan-amount">{t('dashboard.yearlyPlanAmount')}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">$</span>
              <Input
                id="yearly-plan-amount"
                type="number"
                min={1}
                step="any"
                className="pl-7"
                placeholder="10000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={planLoading || mutation.isPending}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 || mutation.isPending}
            >
              {mutation.isPending ? t('saving') : t('save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
